import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Trash2, Phone, Mail, MapPin, Wrench, Loader2, CheckCircle, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;

const STATES = [
  { code: "CA", name: "California" },
  { code: "OR", name: "Oregon" },
  { code: "AZ", name: "Arizona" },
  { code: "WA", name: "Washington" },
  { code: "TX", name: "Texas" },
  { code: "NM", name: "New Mexico" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "SC", name: "South Carolina" },
  { code: "NC", name: "North Carolina" },
  { code: "VA", name: "Virginia" },
  { code: "DC", name: "District of Columbia" },
  { code: "MD", name: "Maryland" },
  { code: "DE", name: "Delaware" },
  { code: "PA", name: "Pennsylvania" },
  { code: "NJ", name: "New Jersey" },
  { code: "NY", name: "New York" },
];
const SERVICES = ["HYBD BATT ONLY", "Full Hybrid Service", "Battery Replacement", "Diagnostic Only", "Other"];

const WARRANTY_ELIGIBLE = new Set([
  "Lexus|CT 200h",
  "Toyota|Prius (2004-2009)", "Toyota|Prius (2010-2015)", "Toyota|Prius C", "Toyota|Prius V (2012-2015)",
  "Nissan|Altima (2007-2011)", "Toyota|Camry (2007-2011)", "Toyota|Prius (2001-2003)", "Toyota|Prius (2016-2020)",
]);
const WARRANTY_OPTIONS = [
  { value: "1 Year", label: "1 Year", price: 0, sub: "Free" },
  { value: "2 Years", label: "2 Years", price: 149, sub: "$149" },
  { value: "3 Years", label: "3 Years", price: 199, sub: "$199" },
];
const isWarrantyEligible = (make, model) => WARRANTY_ELIGIBLE.has(`${make}|${model}`);

const VEHICLE_DATA = {
  "Cadillac": ["Escalade (2008-2013)"],
  "Chevrolet": ["Silverado (2009-2013)", "Tahoe (2007-2013)", "Volt"],
  "Chrysler": ["Aspen (2009)"],
  "Dodge": ["Durango"],
  "Ford": ["Escape (2005-2013)", "C-Max (2013-2018)", "C-Max Energi", "Fusion (2010-2012)", "Fusion (2013-2018)", "Fusion Energi"],
  "GMC": ["Sierra (2007-2013)", "Yukon (2007-2013)"],
  "Honda": ["Accord (2005-2007)", "Civic (2003-2005)", "Civic (2006-2011)", "CRZ (2010-2012)", "Insight (2000-2006)", "Insight (2010-2014)"],
  "Hyundai": ["Sonata (2011-2015)"],
  "Kia": ["Optima (2010-2014)", "Optima (2017-2020)"],
  "Lexus": ["CT 200h", "ES 300h", "GS 450h", "HS 250H", "NX 300H (2015-2019)", "RX 400h", "RX 450h"],
  "Lincoln": ["MKZ (2010-2012)", "MKZ (2013-2018)"],
  "Mazda": ["Tribute (2004-2013)"],
  "Mercury": ["Mariner (2005-2010)", "Milan (2010-2013)"],
  "Nissan": ["Altima (2007-2011)"],
  "Tesla": ["Model 3 (2017-2026)", "Model S (2012-2024)", "Model X (2016-2025)", "Model Y (2020-2024)"],
  "Toyota": ["Avalon (2012-2016)", "Camry (2007-2011)", "Camry (2012-2017)", "Highlander (2006-2009)", "Highlander (2010-2015)", "Highlander (2016-2019)", "Prius (2001-2003)", "Prius (2004-2009)", "Prius (2004-2009) (Gen 2) Inverter", "Prius (2010-2014) (Gen 3) Inverter", "Prius (2010-2015)", "Prius (2016-2020)", "Prius C", "Prius V (2012-2015)", "Rav4 (2016-2018)"]
};

const VEHICLE_PRICES = {
  "Cadillac|Escalade (2008-2013)": "2499",
  "Chevrolet|Silverado (2009-2013)": "2599",
  "Chevrolet|Tahoe (2007-2013)": "2699",
  "Chevrolet|Volt": "0",
  "Chrysler|Aspen (2009)": "2799",
  "Dodge|Durango": "2399",
  "Ford|Escape (2005-2013)": "2899",
  "Ford|C-Max (2013-2018)": "2399",
  "Ford|C-Max Energi": "4499",
  "Ford|Fusion (2010-2012)": "3199",
  "Ford|Fusion (2013-2018)": "2399",
  "Ford|Fusion Energi": "4499",
  "GMC|Sierra (2007-2013)": "2599",
  "GMC|Yukon (2007-2013)": "2499",
  "Honda|Accord (2005-2007)": "1999",
  "Honda|Civic (2003-2005)": "2099",
  "Honda|Civic (2006-2011)": "2099",
  "Honda|CRZ (2010-2012)": "2199",
  "Honda|Insight (2000-2006)": "2099",
  "Honda|Insight (2010-2014)": "2299",
  "Hyundai|Sonata (2011-2015)": "2199",
  "Kia|Optima (2010-2014)": "2199",
  "Kia|Optima (2017-2020)": "2199",
  "Lexus|CT 200h": "1799",
  "Lexus|ES 300h": "2199",
  "Lexus|GS 450h": "2499",
  "Lexus|HS 250H": "2199",
  "Lexus|NX 300H (2015-2019)": "2599",
  "Lexus|RX 400h": "2799",
  "Lexus|RX 450h": "2799",
  "Lincoln|MKZ (2010-2012)": "2399",
  "Lincoln|MKZ (2013-2018)": "2899",
  "Mazda|Tribute (2004-2013)": "2899",
  "Mercury|Mariner (2005-2010)": "2899",
  "Mercury|Milan (2010-2013)": "3199",
  "Nissan|Altima (2007-2011)": "1899",
  "Toyota|Avalon (2012-2016)": "2199",
  "Toyota|Camry (2007-2011)": "1899",
  "Toyota|Camry (2012-2017)": "2199",
  "Toyota|Highlander (2006-2009)": "2799",
  "Toyota|Highlander (2010-2015)": "2799",
  "Toyota|Highlander (2016-2019)": "2799",
  "Toyota|Prius (2001-2003)": "1899",
  "Toyota|Prius (2004-2009)": "1699",
  "Toyota|Prius (2004-2009) (Gen 2) Inverter": "999",
  "Toyota|Prius (2010-2014) (Gen 3) Inverter": "999",
  "Toyota|Prius (2010-2015)": "1799",
  "Toyota|Prius (2016-2020)": "1999",
  "Toyota|Prius C": "1799",
  "Toyota|Prius V (2012-2015)": "1799",
  "Toyota|Rav4 (2016-2018)": "2599",
};

const VEHICLE_MAKES = Object.keys(VEHICLE_DATA).sort();

const BookingSystem = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("closed");
  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [mapsLoaded, setMapsLoaded] = useState(!!window.google?.maps?.places);

  // Load Google Maps Places script
  useEffect(() => {
    if (!GOOGLE_MAPS_KEY) return;
    if (window.google?.maps?.places) { setMapsLoaded(true); return; }
    if (document.getElementById("google-maps-script")) return;
    const script = document.createElement("script");
    script.id = "google-maps-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_KEY}&libraries=places`;
    script.async = true;
    script.onload = () => setMapsLoaded(true);
    document.head.appendChild(script);
  }, []);

  // Setup autocomplete once maps is loaded and input is mounted
  useEffect(() => {
    if (!mapsLoaded || !addressInputRef.current || autocompleteRef.current) return;
    
    const ac = new window.google.maps.places.Autocomplete(addressInputRef.current, {
      types: ["address"],
      componentRestrictions: { country: "us" },
    });
    autocompleteRef.current = ac;
    
    ac.addListener("place_changed", () => {
      const place = ac.getPlace();
      if (!place.address_components) return;
      
      let street_number = "", route = "", city = "", state = "", zip = "";
      for (const comp of place.address_components) {
        const type = comp.types[0];
        if (type === "street_number") street_number = comp.long_name;
        else if (type === "route") route = comp.long_name;
        else if (type === "locality" || type === "sublocality_level_1") city = comp.long_name;
        else if (type === "administrative_area_level_1") state = comp.short_name;
        else if (type === "postal_code") zip = comp.long_name;
      }
      
      setFormData(prev => ({
        ...prev,
        address: `${street_number} ${route}`.trim(),
        city: city,
        state: state || prev.state,
        zip: zip,
      }));
    });
  }, [mapsLoaded]);

  const getPSTToday = () => {
    const pst = new Date(new Date().toLocaleString("en-US", { timeZone: "America/Los_Angeles" }));
    return `${pst.getFullYear()}-${String(pst.getMonth() + 1).padStart(2, "0")}-${String(pst.getDate()).padStart(2, "0")}`;
  };

  // ==================== CLOSED (JOB FORM) STATE ====================
  const [formData, setFormData] = useState({
    lead: "Email", source: "H2G",
    callDate: getPSTToday(),
    installDate: getPSTToday(),
    name: "", lastName: "", phone: "", email: "", confirmNumber: "",
    address: "", city: "", state: "CA", zip: "",
    vehicleYear: "", vehicleMake: "", vehicleModel: "",
    service: "HYBD BATT ONLY", refurbOrNew: "REFURB",
    discount: "0", phonePrice: "0", payPlan: "No", warranty: "", notes: ""
  });
  const [isSaving, setIsSaving] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [savedBooking, setSavedBooking] = useState(null);
  const [customVehicle, setCustomVehicle] = useState(false);

  // ==================== MECHANIC LEAD STATE ====================
  const emptyLead = { shop_name: "", phone: "", email: "", email_confirm: "", city: "", state: "" };
  const [leads, setLeads] = useState([]);
  const [leadForm, setLeadForm] = useState(emptyLead);
  const [isAddingLead, setIsAddingLead] = useState(false);
  const [loadingLeads, setLoadingLeads] = useState(false);

  useEffect(() => {
    if (activeTab === "mechanics") fetchLeads();
  }, [activeTab]);

  const fetchLeads = async () => {
    setLoadingLeads(true);
    try {
      const res = await axios.get(`${API}/mechanic-leads`);
      setLeads(res.data);
    } catch (e) {
      toast.error("Failed to load mechanic leads");
    } finally { setLoadingLeads(false); }
  };

  const addLead = async (e) => {
    e.preventDefault();
    if (!leadForm.shop_name.trim() || !leadForm.phone.trim() || !leadForm.city.trim() || !leadForm.state) {
      toast.error("Please fill in shop name, number, city and state");
      return;
    }
    if (leadForm.email.trim().toLowerCase() !== leadForm.email_confirm.trim().toLowerCase()) {
      toast.error("Email addresses do not match");
      return;
    }
    setIsAddingLead(true);
    try {
      const { email_confirm, ...payload } = leadForm;
      await axios.post(`${API}/mechanic-leads`, { ...payload, email: payload.email.trim() });
      toast.success("Mechanic lead saved to Mechanic Lead Callback sheet");
      setLeadForm(emptyLead);
      fetchLeads();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add mechanic lead");
    } finally { setIsAddingLead(false); }
  };

  const removeLead = async (id) => {
    try {
      await axios.delete(`${API}/mechanic-leads/${id}`);
      setLeads(prev => prev.filter(l => l.id !== id));
      toast.success("Lead removed");
    } catch (e) {
      toast.error("Failed to remove lead");
    }
  };

  // ==================== CLOSED FORM HANDLERS ====================
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${parseInt(month)}/${parseInt(day)}/${year}`;
  };

  const copyToClipboard = (data) => {
    const stateName = STATES.find(s => s.code === data.state)?.name || data.state;
    const textToCopy = `NEW JOB BOOKING

Customer: ${data.name} ${data.lastName}
Phone: ${data.phone}
Email: ${data.email}${data.confirmNumber ? `\nConfirm #: ${data.confirmNumber}` : ''}

Address: ${data.address}${data.city ? `, ${data.city}` : ''}, ${stateName}${data.zip ? ` ${data.zip}` : ''}

Vehicle: ${data.vehicleYear} ${data.vehicleMake} ${data.vehicleModel}
Type: ${data.refurbOrNew}

Lead: ${data.lead}
Source: ${data.source}
Call Date: ${formatDateForDisplay(data.callDate)}
Install Date: ${formatDateForDisplay(data.installDate)}

Discount: $${data.discount}
Phone Price: $${data.phonePrice}
Pay Plan: ${data.payPlan}${isWarrantyEligible(data.vehicleMake, data.vehicleModel) && data.warranty ? `\nWarranty: ${data.warranty}${WARRANTY_OPTIONS.find(o => o.value === data.warranty)?.price ? ` ($${WARRANTY_OPTIONS.find(o => o.value === data.warranty).price})` : ' (Free)'}` : ''}${data.notes ? `\n\nNotes: ${data.notes}` : ''}`;

    // Try modern API first, fallback to textarea method
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(textToCopy)
        .then(() => toast.success("Booking info copied to clipboard!"))
        .catch(() => fallbackCopy(textToCopy));
    } else {
      fallbackCopy(textToCopy);
    }
  };

  const fallbackCopy = (text) => {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      document.execCommand("copy");
      toast.success("Booking info copied to clipboard!");
    } catch (err) {
      toast.error("Could not copy automatically. Please copy manually.");
    }
    document.body.removeChild(textarea);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.lastName || !formData.phone || !formData.email) {
      toast.error("Please fill in all required customer information");
      return;
    }

    // Copy to clipboard FIRST (must happen synchronously within user gesture)
    copyToClipboard(formData);

    setIsSaving(true);
    try {
      const backendData = {
        lead: formData.lead, source: formData.source,
        call_date: formData.callDate, install_date: formData.installDate,
        name: formData.name, last_name: formData.lastName,
        phone: formData.phone, email: formData.email, confirm_number: formData.confirmNumber,
        address: formData.address, city: formData.city, state: formData.state, zip: formData.zip,
        vehicle_year: formData.vehicleYear, vehicle_make: formData.vehicleMake, vehicle_model: formData.vehicleModel,
        service: formData.service, refurb_or_new: formData.refurbOrNew,
        discount: formData.discount, phone_price: formData.phonePrice,
        pay_plan: formData.payPlan, notes: formData.notes,
        warranty: isWarrantyEligible(formData.vehicleMake, formData.vehicleModel) ? formData.warranty : "",
        warranty_price: isWarrantyEligible(formData.vehicleMake, formData.vehicleModel) ? String(WARRANTY_OPTIONS.find(o => o.value === formData.warranty)?.price ?? "") : "",
      };
      await fetch(`${API}/bookings`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(backendData) });
      toast.success("Booking saved & copied to clipboard!");
      setSavedBooking({ ...formData });
      setShowConfirmation(true);
    } catch (error) {
      toast.error("Failed to save booking");
    } finally { setIsSaving(false); }
  };

  const inputClass = "w-full px-3 sm:px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-base";
  const toggleBtn = (active) => `flex-1 py-3 rounded-lg border-2 font-medium transition-colors text-sm sm:text-base ${active ? "bg-blue-50 border-blue-500 text-blue-700" : "bg-white border-gray-300 text-gray-700 hover:border-gray-400"}`;

  const bookAnother = () => {
    setShowConfirmation(false);
    setSavedBooking(null);
    setFormData({
      lead: "Email", source: "H2G",
      callDate: getPSTToday(),
      installDate: getPSTToday(),
      name: "", lastName: "", phone: "", email: "", confirmNumber: "",
      address: "", city: "", state: "CA", zip: "",
      vehicleYear: "", vehicleMake: "", vehicleModel: "",
      service: "HYBD BATT ONLY", refurbOrNew: "REFURB",
      discount: "0", phonePrice: "0", payPlan: "No", warranty: "", notes: ""
    });
    setCustomVehicle(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Button onClick={() => navigate("/h2g")} variant="ghost" size="sm" className="flex items-center gap-1 sm:gap-2 flex-shrink-0 px-2 sm:px-3">
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Back to Home</span>
              </Button>
              <div className="border-l border-gray-300 h-6 hidden sm:block"></div>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">New Job Form</h1>
                <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">Schedule appointment for new customer</p>
              </div>
            </div>
            <Button onClick={() => navigate("/jobs")} className="bg-green-600 hover:bg-green-700 text-xs sm:text-sm flex-shrink-0" size="sm">View All Jobs</Button>
          </div>
        </div>
      </div>

      {/* Tab Toggle */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-4 sm:pt-6">
        <div className="flex gap-0 bg-gray-200 rounded-xl p-1" data-testid="booking-tabs">
          <button
            onClick={() => setActiveTab("closed")}
            className={`flex-1 py-3 px-6 rounded-lg font-semibold text-sm transition-all ${
              activeTab === "closed"
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
            data-testid="tab-closed"
          >
            Closed
          </button>
          <button
            onClick={() => setActiveTab("mechanics")}
            className={`flex-1 py-3 px-6 rounded-lg font-semibold text-sm transition-all relative ${
              activeTab === "mechanics"
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
            data-testid="tab-mechanics"
          >
            Mechanic Leads
          </button>
        </div>
      </div>

      {/* CONFIRMATION SCREEN */}
      {showConfirmation && savedBooking && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sm:p-8 text-center" data-testid="booking-confirmation">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">Job Booked!</h2>
            <p className="text-sm sm:text-base text-gray-500 mb-6">Info copied to clipboard — paste it in your group text</p>

            <div className="bg-gray-50 rounded-xl p-4 sm:p-6 text-left max-w-md mx-auto mb-6">
              <div className="space-y-3">
                <div className="flex justify-between gap-3">
                  <span className="text-sm text-gray-500 flex-shrink-0">Customer</span>
                  <span className="text-sm font-semibold text-gray-900 text-right">{savedBooking.name} {savedBooking.lastName}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-sm text-gray-500 flex-shrink-0">Phone</span>
                  <span className="text-sm font-semibold text-gray-900 text-right">{savedBooking.phone}</span>
                </div>
                {savedBooking.email && (
                  <div className="flex justify-between gap-3">
                    <span className="text-sm text-gray-500 flex-shrink-0">Email</span>
                    <span className="text-sm font-semibold text-gray-900 text-right break-all">{savedBooking.email}</span>
                  </div>
                )}
                {savedBooking.vehicleMake && (
                  <div className="flex justify-between gap-3">
                    <span className="text-sm text-gray-500 flex-shrink-0">Vehicle</span>
                    <span className="text-sm font-semibold text-gray-900 text-right">{savedBooking.vehicleYear} {savedBooking.vehicleMake} {savedBooking.vehicleModel}</span>
                  </div>
                )}
                {savedBooking.address && (
                  <div className="flex justify-between gap-3">
                    <span className="text-sm text-gray-500 flex-shrink-0">Address</span>
                    <span className="text-sm font-semibold text-gray-900 text-right">{savedBooking.address}{savedBooking.city ? `, ${savedBooking.city}` : ''}, {savedBooking.state}</span>
                  </div>
                )}
                <div className="flex justify-between gap-3">
                  <span className="text-sm text-gray-500 flex-shrink-0">Install Date</span>
                  <span className="text-sm font-semibold text-gray-900 text-right">{formatDateForDisplay(savedBooking.installDate)}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-sm text-gray-500 flex-shrink-0">Type</span>
                  <span className="text-sm font-semibold text-gray-900 text-right">{savedBooking.refurbOrNew}</span>
                </div>
              </div>
            </div>

            <button
              onClick={() => copyToClipboard(savedBooking)}
              className="mb-6 px-6 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-lg transition-colors inline-flex items-center gap-2 text-sm"
              data-testid="copy-again-btn"
            >
              <Copy className="w-4 h-4" />
              Copy Info to Clipboard
            </button>

            <div className="flex gap-3 sm:gap-4 max-w-md mx-auto">
              <button
                onClick={bookAnother}
                className="flex-1 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 text-sm sm:text-base"
                data-testid="book-another-btn"
              >
                <Save className="w-4 h-4 sm:w-5 sm:h-5" />
                Book Another
              </button>
              <button
                onClick={() => navigate("/jobs")}
                className="flex-1 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors text-sm sm:text-base"
                data-testid="view-all-jobs-btn"
              >
                View All Jobs
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CLOSED TAB (existing form) */}
      {activeTab === "closed" && !showConfirmation && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-8">
            {/* LEAD */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase">Lead</label>
              <div className="flex gap-2 sm:gap-3">
                <button type="button" onClick={() => handleChange("lead", "Email")} className={toggleBtn(formData.lead === "Email")}>Email</button>
                <button type="button" onClick={() => handleChange("lead", "Phone")} className={toggleBtn(formData.lead === "Phone")}>Phone</button>
              </div>
            </div>

            {/* SOURCE */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase">Source</label>
              <div className="flex gap-2 sm:gap-3">
                <button type="button" onClick={() => handleChange("source", "H2G")} className={toggleBtn(formData.source === "H2G")}>H2G</button>
                <button type="button" onClick={() => handleChange("source", "HBREPAIR")} className={toggleBtn(formData.source === "HBREPAIR")}>HBREPAIR</button>
              </div>
            </div>

            {/* CALL DATE & INSTALL DATE */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase">Call Date</label>
              <input type="date" value={formData.callDate} onChange={(e) => handleChange("callDate", e.target.value)} className="w-full max-w-full box-border px-3 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-base appearance-none" />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase">Install</label>
              <input type="date" value={formData.installDate} onChange={(e) => handleChange("installDate", e.target.value)} className="w-full max-w-full box-border px-3 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-base appearance-none" />
            </div>

            {/* NAME */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase">Name</label>
              <input type="text" value={formData.name} onChange={(e) => handleChange("name", e.target.value)} className={inputClass} required />
            </div>

            {/* LAST NAME */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase">Last Name</label>
              <input type="text" value={formData.lastName} onChange={(e) => handleChange("lastName", e.target.value)} className={inputClass} required />
            </div>

            {/* PHONE */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase">Phone</label>
              <input type="tel" value={formData.phone} onChange={(e) => handleChange("phone", e.target.value)} className={inputClass} required />
            </div>

            {/* CONFIRM NUMBER */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase">Confirm Number</label>
              <input type="text" inputMode="numeric" pattern="[0-9]*" value={formData.confirmNumber} onChange={(e) => handleChange("confirmNumber", e.target.value)} className={inputClass} />
            </div>

            {/* EMAIL */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase">Email</label>
              <input type="email" value={formData.email} onChange={(e) => handleChange("email", e.target.value)} className={inputClass} required />
            </div>

            {/* ADDRESS */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase">Address</label>
              <input type="text" ref={addressInputRef} value={formData.address} onChange={(e) => handleChange("address", e.target.value)} className={inputClass} placeholder="Start typing an address..." />
            </div>

            {/* CITY */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase">City</label>
              <input type="text" value={formData.city} onChange={(e) => handleChange("city", e.target.value)} className={inputClass} />
            </div>

            {/* STATE */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase">State</label>
              <select
                value={formData.state}
                onChange={(e) => handleChange("state", e.target.value)}
                className={`${inputClass} bg-white`}
              >
                <option value="">Select state...</option>
                {STATES.map(s => (
                  <option key={s.code} value={s.code}>{s.name} ({s.code})</option>
                ))}
              </select>
            </div>

            {/* ZIP */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase">Zip</label>
              <input type="text" value={formData.zip} onChange={(e) => handleChange("zip", e.target.value)} className={inputClass} placeholder="0" />
            </div>

            {/* VEHICLE YEAR */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase">Vehicle Year</label>
              <input type="number" value={formData.vehicleYear} onChange={(e) => handleChange("vehicleYear", e.target.value)} className={inputClass} placeholder="0" min="1900" max="2099" />
            </div>

            {/* VEHICLE MAKE */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase">Vehicle Make</label>
              {customVehicle ? (
                <input type="text" value={formData.vehicleMake} onChange={(e) => { handleChange("vehicleMake", e.target.value); handleChange("phonePrice", "0"); }} className={inputClass} placeholder="e.g. Honda" />
              ) : (
                <select value={formData.vehicleMake} onChange={(e) => { handleChange("vehicleMake", e.target.value); handleChange("vehicleModel", ""); handleChange("phonePrice", "0"); }} className={`${inputClass} bg-white`}>
                  <option value="">Select...</option>
                  {VEHICLE_MAKES.map(make => <option key={make} value={make}>{make}</option>)}
                </select>
              )}
            </div>

            {/* VEHICLE MODEL */}
            <div className="mb-3">
              <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase">Vehicle Model</label>
              {customVehicle ? (
                <input type="text" value={formData.vehicleModel} onChange={(e) => handleChange("vehicleModel", e.target.value)} className={inputClass} placeholder="e.g. Accord 2005-2007" />
              ) : (
                <select value={formData.vehicleModel} onChange={(e) => {
                  const model = e.target.value;
                  handleChange("vehicleModel", model);
                  const priceKey = `${formData.vehicleMake}|${model}`;
                  const price = VEHICLE_PRICES[priceKey];
                  if (price !== undefined) handleChange("phonePrice", price);
                }} className={`${inputClass} bg-white`} disabled={!formData.vehicleMake}>
                  <option value="">Select...</option>
                  {formData.vehicleMake && VEHICLE_DATA[formData.vehicleMake]?.map(model => <option key={model} value={model}>{model}</option>)}
                </select>
              )}
            </div>
            <button
              type="button"
              onClick={() => { setCustomVehicle(!customVehicle); handleChange("vehicleMake", ""); handleChange("vehicleModel", ""); handleChange("phonePrice", "0"); }}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium mb-4 py-3 block min-h-[44px]"
              data-testid="toggle-custom-vehicle"
            >
              {customVehicle ? "Back to vehicle list" : "Vehicle not listed? Enter manually"}
            </button>

            {/* REFURB OR NEW */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase">Refurb or New</label>
              <div className="flex gap-2 sm:gap-3">
                <button type="button" onClick={() => handleChange("refurbOrNew", "REFURB")} className={toggleBtn(formData.refurbOrNew === "REFURB")}>REFURB</button>
                <button type="button" onClick={() => handleChange("refurbOrNew", "NEW")} className={toggleBtn(formData.refurbOrNew === "NEW")}>NEW</button>
              </div>
            </div>

            {/* DISCOUNT */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase">Discount</label>
              <div className="relative">
                <span className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-gray-600 font-medium">$</span>
                <input type="number" value={formData.discount} onChange={(e) => handleChange("discount", e.target.value)} className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-base" min="0" step="0.01" />
              </div>
            </div>

            {/* PHONE PRICE */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase">Phone Price</label>
              <div className="relative">
                <span className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-gray-600 font-medium">$</span>
                <input type="number" value={formData.phonePrice} onChange={(e) => handleChange("phonePrice", e.target.value)} className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-base" min="0" step="0.01" />
              </div>
            </div>

            {/* PAY PLAN */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase">Pay Plan</label>
              <div className="flex gap-2 sm:gap-3">
                <button type="button" onClick={() => handleChange("payPlan", "Yes")} className={toggleBtn(formData.payPlan === "Yes")}>Yes</button>
                <button type="button" onClick={() => handleChange("payPlan", "No")} className={toggleBtn(formData.payPlan === "No")}>No</button>
              </div>
            </div>

            {/* WARRANTY (eligible vehicles only) */}
            {isWarrantyEligible(formData.vehicleMake, formData.vehicleModel) && (
              <div className="mb-6 p-4 rounded-lg border-2 border-emerald-200 bg-emerald-50/60" data-testid="warranty-box">
                <label className="block text-sm font-semibold text-emerald-800 mb-1 uppercase">Warranty</label>
                <p className="text-xs text-emerald-700 mb-3">Available for {formData.vehicleMake} {formData.vehicleModel}</p>
                <div className="grid grid-cols-3 gap-2 sm:gap-3">
                  {WARRANTY_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => handleChange("warranty", formData.warranty === opt.value ? "" : opt.value)}
                      className={`py-3 px-2 rounded-lg border-2 font-medium transition-colors text-sm sm:text-base flex flex-col items-center ${formData.warranty === opt.value ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-gray-300 text-gray-700 hover:border-emerald-400"}`}
                      data-testid={`warranty-option-${opt.price}`}
                    >
                      <span className="font-semibold">{opt.label}</span>
                      <span className={`text-xs mt-0.5 ${formData.warranty === opt.value ? "text-emerald-100" : "text-gray-500"}`}>{opt.sub}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* NOTES */}
            <div className="mb-8">
              <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase">Notes</label>
              <textarea value={formData.notes} onChange={(e) => handleChange("notes", e.target.value)} className={`${inputClass} resize-none`} rows="4" />
            </div>

            {/* ACTION BUTTONS */}
            <div className="flex gap-2 sm:gap-4">
              <button type="button" onClick={() => navigate("/h2g")} className="flex-1 py-3 border-2 border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors text-sm sm:text-base">Cancel</button>
              <button type="submit" disabled={isSaving} className="flex-1 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-sm sm:text-base">
                {isSaving ? "Saving..." : <><Save className="w-5 h-5" />Save</>}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* MECHANIC LEADS TAB */}
      {activeTab === "mechanics" && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <form onSubmit={addLead} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6" data-testid="mechanic-lead-form">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Mechanic Lead Callback</h3>
            <p className="text-sm text-gray-500 mb-4">Shops to call back — saved here and to your "Mechanic Lead Callback" Google Sheet</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wider">Shop Name *</label>
                <input
                  type="text"
                  value={leadForm.shop_name}
                  onChange={(e) => setLeadForm(prev => ({ ...prev, shop_name: e.target.value }))}
                  className={inputClass}
                  placeholder="e.g. Joe's Auto Repair"
                  data-testid="lead-shop-name-input"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wider">Number *</label>
                <input
                  type="tel"
                  value={leadForm.phone}
                  onChange={(e) => setLeadForm(prev => ({ ...prev, phone: e.target.value }))}
                  className={inputClass}
                  placeholder="Shop phone number"
                  data-testid="lead-phone-input"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wider">Email</label>
                <input
                  type="email"
                  value={leadForm.email}
                  onChange={(e) => setLeadForm(prev => ({ ...prev, email: e.target.value }))}
                  className={inputClass}
                  placeholder="shop@example.com"
                  data-testid="lead-email-input"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wider">Confirm Email</label>
                <input
                  type="email"
                  value={leadForm.email_confirm}
                  onChange={(e) => setLeadForm(prev => ({ ...prev, email_confirm: e.target.value }))}
                  onPaste={(e) => e.preventDefault()}
                  autoComplete="off"
                  className={`${inputClass} ${leadForm.email_confirm && leadForm.email.trim().toLowerCase() !== leadForm.email_confirm.trim().toLowerCase() ? "border-red-400 focus:ring-red-200" : ""}`}
                  placeholder="Re-type the email"
                  data-testid="lead-email-confirm-input"
                />
                {leadForm.email_confirm && leadForm.email.trim().toLowerCase() !== leadForm.email_confirm.trim().toLowerCase() && (
                  <p className="text-xs text-red-500 mt-1" data-testid="lead-email-mismatch">Emails don't match</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wider">City *</label>
                <input
                  type="text"
                  value={leadForm.city}
                  onChange={(e) => setLeadForm(prev => ({ ...prev, city: e.target.value }))}
                  className={inputClass}
                  placeholder="City"
                  data-testid="lead-city-input"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wider">State *</label>
                <select
                  value={leadForm.state}
                  onChange={(e) => setLeadForm(prev => ({ ...prev, state: e.target.value }))}
                  className={`${inputClass} bg-white`}
                  data-testid="lead-state-select"
                >
                  <option value="">Select state...</option>
                  {STATES.map(st => <option key={st.code} value={st.code}>{st.name} ({st.code})</option>)}
                </select>
              </div>
            </div>
            <Button
              type="submit"
              disabled={isAddingLead}
              className="bg-orange-500 hover:bg-orange-600 text-white font-semibold"
              data-testid="add-lead-button"
            >
              {isAddingLead ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wrench className="w-4 h-4 mr-2" />}
              Add Mechanic Lead
            </Button>
          </form>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Callback List</h3>
                <p className="text-sm text-gray-500" data-testid="lead-count">
                  {leads.length} shop{leads.length !== 1 ? "s" : ""} to call back
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={fetchLeads} data-testid="refresh-leads">
                Refresh
              </Button>
            </div>

            {loadingLeads ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : leads.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <Wrench className="w-12 h-12 mb-3 opacity-40" />
                <p className="font-medium text-gray-500">No mechanic leads yet</p>
                <p className="text-sm">Add a shop above to start your callback list</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {leads.map((lead, index) => (
                  <div key={lead.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors" data-testid={`lead-row-${index}`}>
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="w-9 h-9 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-bold text-sm flex-shrink-0">
                        {index + 1}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900 truncate">{lead.shop_name}</p>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 mt-0.5">
                          <a href={`tel:${lead.phone}`} className="flex items-center gap-1 hover:text-orange-600">
                            <Phone className="w-3.5 h-3.5" />
                            {lead.phone}
                          </a>
                          {lead.email && (
                            <a href={`mailto:${lead.email}`} className="flex items-center gap-1 hover:text-orange-600 truncate">
                              <Mail className="w-3.5 h-3.5" />
                              {lead.email}
                            </a>
                          )}
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5" />
                            {lead.city}, {lead.state}
                          </span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => removeLead(lead.id)}
                      className="text-gray-400 hover:text-red-500 p-2 rounded-lg hover:bg-red-50 transition-colors flex-shrink-0"
                      data-testid={`remove-lead-${index}`}
                      aria-label="Remove lead"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingSystem;
